import type { SupervisorFinding } from '@avisoma/shared';
import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../catalyst/badge';
import { formatDateTime } from '../lib/dateFormat';
import { isRevertable } from '../lib/findingActions';
import { FINDING_STATUS_COLOR } from '../lib/findingStatusColor';
import { formatValue } from '../lib/formatValue';
import { FindingRevertButton } from './FindingRevertButton';
import { FindingRevertContext } from './supervisorFindingRevert';

const POPOVER_W = 320; // w-80
const GAP = 6;
const MARGIN = 8;
/** Time to move the pointer from the badge across the gap into an interactive popover. */
const HIDE_DELAY_MS = 200;

type Props = { finding: SupervisorFinding; compact?: boolean };

/**
 * Status pill for one supervisor finding; hover or keyboard focus shows the before/after
 * values and the agent's reason in a popover. Portalled like `HelpTip` so it escapes the
 * extraction tables' scroll containers without clipping.
 */
export function SupervisorFindingBadge({ finding, compact = false }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  // Inside an entity view (context provided), an applied or reapplied finding's popover carries Revert.
  const revert = useContext(FindingRevertContext);
  const interactive = revert !== null && isRevertable(finding.status);
  const cancelHide = () => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const show = () => {
    cancelHide();
    setAnchor(ref.current?.getBoundingClientRect() ?? null);
  };
  const hide = () => {
    cancelHide();
    if (interactive) hideTimer.current = setTimeout(() => setAnchor(null), HIDE_DELAY_MS);
    else setAnchor(null);
  };
  // The popover is `position: fixed` at the position read on show, and the tables scroll
  // inside their own containers — hide on any scroll (capture phase) instead of drifting.
  useEffect(() => {
    if (anchor === null) return;
    const onScroll = () => setAnchor(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [anchor]);
  useEffect(() => {
    const timer = hideTimer;
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);
  const proposal = finding.hasProposal ? formatValue(finding.newValue) : 'no proposal';
  const summary = `${finding.field}: ${formatValue(finding.oldValue)} → ${proposal}. ${finding.reason}`;

  return (
    <span
      ref={ref}
      tabIndex={0}
      role="img"
      aria-label={summary}
      className="inline-flex items-center gap-1 cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(e) => {
        // Focus moving onto the popover's Revert button keeps the popover open.
        if (!popoverRef.current?.contains(e.relatedTarget as Node | null)) hide();
      }}
    >
      <Badge color={FINDING_STATUS_COLOR[finding.status]}>{finding.status}</Badge>
      {!compact && <span className="font-mono text-xs text-gray-900 dark:text-zinc-100">{finding.field}</span>}
      {anchor !== null &&
        createPortal(
          // z-[80]: the badge also renders inside the row inspector, a Catalyst dialog at z-[70].
          <div
            ref={popoverRef}
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
            className={clsx(
              'fixed z-[80] rounded-md border border-gray-200 bg-white p-3 text-xs leading-snug text-gray-600 shadow-lg dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
              interactive ? 'pointer-events-auto cursor-default' : 'pointer-events-none',
            )}
            style={{
              // Open below the badge, or above it when the badge sits in the lower half of the viewport.
              ...(anchor.top > window.innerHeight / 2
                ? { bottom: window.innerHeight - anchor.top + GAP }
                : { top: anchor.bottom + GAP }),
              left: Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - POPOVER_W - MARGIN)),
              width: POPOVER_W,
            }}
          >
            <dl className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-1">
              <dt className="text-gray-500 dark:text-zinc-400">Field</dt>
              <dd className="font-mono break-all text-gray-900 dark:text-zinc-100">
                {finding.family ? `${finding.family}.` : ''}
                {finding.field}
              </dd>
              <dt className="text-gray-500 dark:text-zinc-400">Old</dt>
              <dd className="font-mono break-all text-gray-900 dark:text-zinc-100">{formatValue(finding.oldValue)}</dd>
              <dt className="text-gray-500 dark:text-zinc-400">New</dt>
              <dd className="font-mono break-all text-gray-900 dark:text-zinc-100">{proposal}</dd>
              <dt className="text-gray-500 dark:text-zinc-400">Reason</dt>
              <dd>{finding.reason}</dd>
              <dt className="text-gray-500 dark:text-zinc-400">Check</dt>
              <dd className="font-mono">
                {finding.checkId} v{finding.checkVersion}
              </dd>
              <dt className="text-gray-500 dark:text-zinc-400">Judge</dt>
              <dd>
                {finding.model ?? '—'}
                {finding.confidence !== null ? ` · ${finding.confidence.toFixed(2)}` : ''}
              </dd>
              <dt className="text-gray-500 dark:text-zinc-400">Actor</dt>
              <dd>
                {finding.actor} · {formatDateTime(finding.createdAt)}
              </dd>
            </dl>
            {interactive && (
              <div className="mt-2 flex justify-end">
                <FindingRevertButton finding={finding} handle={revert} />
              </div>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
}
